import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import '../../css/growthWheel.scss'
import {
  type GrowthWheelUserForReview,
  useGetGrowthWheelByRoleIdQuery,
  useGetGrowthWheelWithEvaluationsByUserIdQuery,
  useGetGrowthWheelUsersByGrowthWheelReviewerIdQuery,
} from '../../redux/growth-wheel.endpoints'
import {
  setGrowthWheelData,
  setUserCompetenciesData,
  setLoadingGrowthWheel,
  setCurrentCompetency,
  setCurrentSubCompetency,
  evaluateFocusCompetenciesLimit,
  evaluateGrowthWheelCompletion,
  setAssessmentCompletion,
  setCurrentAreasOfFocus,
} from '../../redux/growthWheel.slice'
import { RootState } from '../../redux/store'
import { User } from '../../types/User'
import { AdminContributorList } from '../../components/GrowthWheelComponents/AdminContributorList'
import BackButton from '../../components/GrowthWheelComponents/BackButton'
import GrowthWheelDashboard from '../GrowthWheelDashboard/GrowthWheelDashboard'
import GrowthWheelCompetencyAreaView from '../GrowthWheelCompetencyView/GrowthWheelCompetencyView'
import GrowthWheelCompetencyAssessment from '../GrowthWheelSubCompetencyView/GrowthWheelSubCompetencyView'

const GrowthWheelAdmin = ({ user }: { user: User }) => {
  const dispatch = useDispatch()
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [selectedReviewerGuildId, setselectedReviewerGuildId] = useState<
    number | null
  >(null)

  const { data: contributorsData } =
    useGetGrowthWheelUsersByGrowthWheelReviewerIdQuery(
      { reviewerId: user.id },
      { refetchOnMountOrArgChange: true },
    )

  const selectedUser = contributorsData?.find(c => c.id === selectedUserId)

  const roleId =
    selectedUserId != null
      ? (selectedReviewerGuildId ??
          selectedUser?.guildId ??
          user.guildId)
      : user.guildId

  const { data: growthWheelData, isLoading } = useGetGrowthWheelByRoleIdQuery(
    { roleId },
    { skip: !selectedUserId, refetchOnMountOrArgChange: true },
  )
  const { data: userAssessmentData } =
    useGetGrowthWheelWithEvaluationsByUserIdQuery(
      { userId: selectedUserId ?? 0 },
      { skip: !selectedUserId, refetchOnMountOrArgChange: true },
    )
  const userCompetenciesData = userAssessmentData?.competencies

  const currentCompetency = useSelector(
    (state: RootState) => state.growthWheel.currentCompetency,
  )
  const currentSubCompetency = useSelector(
    (state: RootState) => state.growthWheel.currentSubCompetency,
  )

  useEffect(() => {
    dispatch(setGrowthWheelData(growthWheelData ?? null))
  }, [dispatch, growthWheelData])

  useEffect(() => {
    dispatch(setUserCompetenciesData(userCompetenciesData ?? []))
    dispatch(evaluateFocusCompetenciesLimit())
    dispatch(evaluateGrowthWheelCompletion())
    dispatch(setCurrentAreasOfFocus())
    dispatch(
      setAssessmentCompletion({
        isCompleted: userAssessmentData?.isCompleted ?? false,
        completedAt: userAssessmentData?.completedAt ?? null,
      }),
    )
  }, [dispatch, userCompetenciesData, userAssessmentData])

  useEffect(() => {
    dispatch(setLoadingGrowthWheel(isLoading))
  }, [dispatch, isLoading])

  useEffect(() => {
    if (!userCompetenciesData?.length) return
    if (currentCompetency) {
      const freshCompetency = userCompetenciesData.find(
        c => c.id === currentCompetency.id,
      )
      if (freshCompetency) {
        dispatch(setCurrentCompetency(freshCompetency))
        if (currentSubCompetency) {
          const freshSub = freshCompetency.subCompetencies?.find(
            s => s.id === currentSubCompetency.id,
          )
          if (freshSub) dispatch(setCurrentSubCompetency(freshSub))
        }
      }
    }
  }, [userCompetenciesData, currentCompetency, currentSubCompetency, dispatch])

  const handleSelectContributor = (contributor: GrowthWheelUserForReview) => {
    setSelectedUserId(contributor.id)
    setselectedReviewerGuildId(contributor.guildId)
    dispatch(setCurrentCompetency(null))
    dispatch(setCurrentSubCompetency(null))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleBackToContributors = () => {
    setSelectedUserId(null)
    setselectedReviewerGuildId(null)
    dispatch(setCurrentCompetency(null))
    dispatch(setCurrentSubCompetency(null))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const displayAssessmentView = currentSubCompetency && currentCompetency
  const displayCompetencyView = currentCompetency && !currentSubCompetency
  const displayDashboardView = !currentCompetency && growthWheelData

  const reviewedUserName = selectedUser
    ? `${selectedUser.firstName} ${selectedUser.lastName}`
    : ''
  const reviewedGuildName = selectedUser?.area ?? ''

  return (
    <main>
      {!selectedUserId ? (
        <AdminContributorList
          contributors={contributorsData ?? []}
          onSelectContributor={handleSelectContributor}
        />
      ) : (
        <div className="container mb-5 gw-growth-wheel-container">
        {selectedUserId &&
          !currentCompetency &&
          !currentSubCompetency && (
            <div className="my-5">
              <BackButton
                onClick={handleBackToContributors}
                text="Back to team members"
              />
            </div>
          )}

          {displayAssessmentView && selectedUserId != null && (
            <GrowthWheelCompetencyAssessment
              subjectUserId={selectedUserId}
              reviewerUserId={user.id}
              isAdminView={true}
              userName={reviewedUserName}
              guildName={reviewedGuildName}
            />
          )}
          {displayCompetencyView && (
            <GrowthWheelCompetencyAreaView
              subjectUserId={selectedUserId}
              userName={reviewedUserName}
              guildName={reviewedGuildName}
            />
          )}
          {displayDashboardView && (
            <GrowthWheelDashboard
              isAdminView={true}
              userId={selectedUserId}
              userName={reviewedUserName}
              guildName={reviewedGuildName}
            />
          )}
        </div>
      )}
    </main>
  )
}

export default GrowthWheelAdmin
